import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ChevronUp } from "lucide-react";

const ScrollTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const lastScrollY = useRef(0);
  const fadeTimeoutRef = useRef<NodeJS.Timeout>();

  // Show button when page is scrolled down AND user starts scrolling up
  const toggleVisibility = () => {
    const currentScrollY = window.scrollY;
    const isScrollingUp = currentScrollY < lastScrollY.current;

    if (currentScrollY > 300 && isScrollingUp) {
      setIsVisible(true);
      setIsFading(false);

      // Clear any existing timeout
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }

      // Set new timeout to fade out button
      fadeTimeoutRef.current = setTimeout(() => {
        setIsFading(true);
        // Hide button after fade animation completes
        setTimeout(() => setIsVisible(false), 300);
      }, 1000);
    }

    lastScrollY.current = currentScrollY;
  };

  // Scroll to top smoothly
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", toggleVisibility);
    return () => {
      window.removeEventListener("scroll", toggleVisibility);
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <Button
      variant="secondary"
      size="icon"
      className={`fixed dark top-16 right-4 md:right-8 md:top-20 rounded-full p-2 shadow-lg hover:shadow-xl z-50 bg-opacity-90 transition-opacity duration-300 ${
        isFading ? "opacity-0" : "opacity-100"
      }`}
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      <ChevronUp className="h-6 w-6" />
    </Button>
  );
};

export default ScrollTopButton;
